import React, { useEffect } from 'react';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { DARK_THEME } from '../../../constants';
import { useResponsive } from '../hooks/useResponsive';
import { useDispatch } from 'react-redux';
import { setAddingTextStatus } from '../../../redux-tool/slice/toolSlice';

const MobileTextModal = ({
  showFloatingInput,
  setShowFloatingInput,
  text,
  setText,
  activeTextId,
  isSubmitting,
  inputRef,
  focusInput,
  setShouldMaintainFocus,
  handleSubmit,
  handleUpdateText,
  findTextByIdMemo,
  textList,
}) => {
  // Get responsive info
  const { isMobile, isTablet } = useResponsive();
  const dispatch = useDispatch();

  useEffect(() => {
    if (showFloatingInput) {
      dispatch(setAddingTextStatus(true));
    } else {
      dispatch(setAddingTextStatus(false));
    }
  }, [showFloatingInput]);

  if (!showFloatingInput) {
    return null;
  }

  return (
    <>
      <style>
        {`
          /* Mobile Floating Input Styles */
          .floating-input-mobile {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: rgba(0, 0, 0, 0.95) !important;
            backdrop-filter: blur(10px) !important;
            -webkit-backdrop-filter: blur(10px) !important;
            z-index: 1000 !important;
            pointer-events: auto !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            max-width: none !important;
            min-width: none !important;
          }
          
          /* Ensure input is properly styled on mobile */
          .floating-input-mobile input,
          .floating-input-mobile textarea {
            font-size: 18px !important;
            -webkit-appearance: none !important;
            border-radius: 0 !important;
            background: transparent !important;
            color: #fff !important;
          }
          
          /* Prevent zoom on input focus for mobile */
          .floating-input-mobile input:focus,
          .floating-input-mobile textarea:focus {
            font-size: 18px !important;
            transform: none !important;
          }
        `}
      </style>
      <div id="floating-text-input-mobile" className="floating-input-mobile">
        {/* Top-right action buttons (Done + Close) */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 1001,
            display: 'flex',
            gap: '12px',
          }}
        >
          {text.trim() ? (
            <Button
              variant="primary"
              onClick={() => {
                if (text.trim() && !isSubmitting) {
                  if (activeTextId) {
                    // Update existing text only if it has changed
                    const currentText = findTextByIdMemo(activeTextId)?.Text;
                    if (text.trim() !== currentText) {
                      handleUpdateText();
                    }
                  } else {
                    // Add new text - only if not already processing
                    if (
                      !textList.some((item) => item.Text === text.trim() && item.isNew === true)
                    ) {
                      handleSubmit();
                    }
                  }
                }
                // Always close the overlay
                setShowFloatingInput(false);
              }}
              disabled={!text.trim() || isSubmitting}
              style={{
                padding: '12px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '600',
                minWidth: '56px',
                minHeight: '32px',
                width: '56px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: text.trim() && !isSubmitting ? 1 : 0.5,
                background:
                  text.trim() && !isSubmitting
                    ? `linear-gradient(135deg, ${DARK_THEME.accent.primary} 0%, ${DARK_THEME.accent.secondary} 100%)`
                    : 'rgba(255,255,255,0.1)',
                border: text.trim() && !isSubmitting ? 'none' : `1px solid rgba(255,255,255,0.2)`,
                cursor: text.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                boxShadow:
                  text.trim() && !isSubmitting ? `0 8px 24px rgba(43, 255, 255, 0.4)` : 'none',
                transition: 'all 0.3s ease',
                transform: text.trim() && !isSubmitting ? 'scale(1)' : 'scale(0.95)',
                color: text.trim() && !isSubmitting ? '#000' : 'rgb(253, 253, 253)',
              }}
            >
              {isSubmitting ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid currentColor',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setShowFloatingInput(false)}
              style={{
                padding: '12px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '600',
                minWidth: '56px',
                minHeight: '32px',
                width: '56px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.1)',
                border: `1px solid rgba(255,255,255,0.2)`,
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              aria-label="Close text modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block' }}
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          )}
        </div>

        {/* Text Input - Centered */}
        <div
          style={{
            width: '90%',
            maxWidth: '500px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          {/* Input Container */}
          <div
            onClick={() => focusInput()}
            style={{
              width: '100%',
              position: 'relative',
              padding: '20px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s ease',
            }}
          >
            <TextInput
              value={text}
              placeholder="Enter text here"
              multiline={true}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Desktop: Shift+Enter for new line, Enter to submit
                  // Mobile/Tablet: Enter for new line, no submit on Enter
                  if (isMobile || isTablet) {
                    // Mobile/Tablet: Enter creates new line
                    e.preventDefault();
                    setText(text + '\n');
                  } else {
                    // Desktop: Shift+Enter for new line, Enter to submit
                    if (e.shiftKey) {
                      // Shift+Enter: Add new line
                      e.preventDefault();
                      setText(text + '\n');
                    } else {
                      // Enter: Submit
                      e.preventDefault();
                      e.stopPropagation();
                      if ((!activeTextId || activeTextId === '') && !isSubmitting) {
                        handleSubmit();
                        setShowFloatingInput(false);
                      }
                    }
                  }
                }
              }}
              onBlur={(e) => {
                if (text.trim() && !e.currentTarget.contains(e.relatedTarget)) {
                  const target = e.relatedTarget;
                  if (
                    target &&
                    (target.closest('.MuiSlider-root') ||
                      target.closest('.color-swatch') ||
                      target.closest('.font-button') ||
                      target.closest('.clickable'))
                  ) {
                    setShouldMaintainFocus(true);
                    return;
                  }
                  // Only submit on blur if not already processing, no active text, and not submitting
                  if ((!activeTextId || activeTextId === '') && !isSubmitting) {
                    handleSubmit();
                    setShowFloatingInput(false);
                  }
                }
              }}
              onFocus={() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              ref={inputRef}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#fff',
                fontSize: '18px',
                fontWeight: '400',
                lineHeight: '1.5',
                resize: 'none',
                minHeight: '60px',
                maxHeight: '200px',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileTextModal;
